import {
  PrivacyStatus,
  VerificationStatus,
  PrivacyJurisdiction,
  STATUS_LABELS,
  VERIFICATION_STATUS_LABELS,
  JURISDICTION_LABELS,
} from '@/models/privacy'
import { Tooltip } from '@/components/ui'
import { PRIVACY_HELP } from '../constants/help-content'

interface BadgeProps {
  className?: string
  showTooltip?: boolean
}

const baseStyles = 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium'

// Privacy Status Badge
interface StatusBadgeProps extends BadgeProps {
  status: PrivacyStatus
}

const statusStyles: Record<PrivacyStatus, string> = {
  received: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  denied: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
}

export function StatusBadge({ status, className = '', showTooltip = true }: StatusBadgeProps) {
  const badge = (
    <span className={`${baseStyles} ${statusStyles[status]} ${className}`}>
      {STATUS_LABELS[status]}
    </span>
  )

  if (!showTooltip) return badge

  return (
    <Tooltip content={PRIVACY_HELP.status[status]} position="top">
      {badge}
    </Tooltip>
  )
}

// Verification Status Badge
interface VerificationBadgeProps extends BadgeProps {
  status: VerificationStatus
}

const verificationStyles: Record<VerificationStatus, string> = {
  pending: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300',
  verified: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
}

export function VerificationBadge({ status, className = '', showTooltip = true }: VerificationBadgeProps) {
  const badge = (
    <span className={`${baseStyles} ${verificationStyles[status]} ${className}`}>
      {VERIFICATION_STATUS_LABELS[status]}
    </span>
  )

  if (!showTooltip) return badge

  return (
    <Tooltip content={PRIVACY_HELP.verification[status]} position="top">
      {badge}
    </Tooltip>
  )
}

// Jurisdiction Badge
interface JurisdictionBadgeProps extends BadgeProps {
  jurisdiction: PrivacyJurisdiction
}

const jurisdictionStyles: Record<PrivacyJurisdiction, string> = {
  ccpa: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  gdpr: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  other: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300',
}

export function JurisdictionBadge({ jurisdiction, className = '', showTooltip = true }: JurisdictionBadgeProps) {
  const badge = (
    <span className={`${baseStyles} ${jurisdictionStyles[jurisdiction]} ${className}`}>
      {JURISDICTION_LABELS[jurisdiction]}
    </span>
  )

  if (!showTooltip) return badge

  return (
    <Tooltip content={PRIVACY_HELP.jurisdiction[jurisdiction]} position="top">
      {badge}
    </Tooltip>
  )
}

// Request Type Indicators
interface RequestTypesProps extends BadgeProps {
  optOutSale: boolean
  optOutShare: boolean
  removeData: boolean
  doNotContact: boolean
}

export function RequestTypes({
  optOutSale,
  optOutShare,
  removeData,
  doNotContact,
  className = '',
  showTooltip = true,
}: RequestTypesProps) {
  const types = [
    { active: optOutSale, label: 'Opt-Out Sale', abbrev: 'S', help: PRIVACY_HELP.requestTypes.opt_out_sale },
    { active: optOutShare, label: 'Opt-Out Share', abbrev: 'Sh', help: PRIVACY_HELP.requestTypes.opt_out_share },
    { active: removeData, label: 'Remove Data', abbrev: 'R', help: PRIVACY_HELP.requestTypes.remove_data },
    { active: doNotContact, label: 'Do Not Contact', abbrev: 'D', help: PRIVACY_HELP.requestTypes.do_not_contact },
  ]

  const activeTypes = types.filter((t) => t.active)

  if (activeTypes.length === 0) {
    return <span className="text-slate-400 text-sm">None</span>
  }

  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {activeTypes.map((type) => {
        const badge = (
          <span
            key={type.abbrev}
            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300"
          >
            {type.abbrev}
          </span>
        )

        if (!showTooltip) return badge

        return (
          <Tooltip key={type.abbrev} content={type.help} position="right">
            {badge}
          </Tooltip>
        )
      })}
    </div>
  )
}
